/**
 * Reusable "Import lab report" flow used by BOTH the clinician (Labs) and the
 * parent (Journal) screens.
 *
 * Pipeline: file picker → POST /attachments (multipart) → POST /labs/extract →
 * editable NFS preset form pre-filled from the (DRAFT) suggestions → POST
 * /labs/results/import. The server decides whether the imported result is signed
 * (clinician) or saved as pending/unverified (parent) and reports that via
 * `needsVerification` on the response.
 *
 * App-specific labels that are not in the shared i18n catalog use a tiny inline
 * FR/EN map keyed off the current locale.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ANALYTE_KEYS,
  ANALYTE_UNITS,
  LAB_STATUSES,
  type AnalyteKey,
  type AnalyteUnit,
} from '@toxofollow/shared';
import { api, getCsrfToken, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Field, inputClass, Table } from './ui.js';
import { Select, ErrorState } from './form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  title: { fr: 'Importer un compte rendu', en: 'Import a lab report' },
  pick: { fr: 'Choisir un fichier (PDF ou photo)', en: 'Choose a file (PDF or photo)' },
  drop: { fr: 'Glissez-déposez un fichier ici', en: 'Drag & drop a file here' },
  dropOr: { fr: 'ou', en: 'or' },
  dropActive: { fr: 'Déposez pour importer', en: 'Drop to import' },
  unsupportedFile: { fr: 'Type de fichier non pris en charge (PDF ou image).', en: 'Unsupported file type (PDF or image).' },
  uploading: { fr: 'Téléversement…', en: 'Uploading…' },
  extracting: { fr: 'Analyse du document…', en: 'Reading the document…' },
  draftNotice: { fr: 'Vérifiez les valeurs extraites', en: 'Verify the extracted values' },
  draftHint: {
    fr: 'Les valeurs ci-dessous sont un brouillon issu du document. Corrigez-les avant d’enregistrer.',
    en: 'The values below are a draft from the document. Correct them before saving.',
  },
  manualEntry: { fr: 'Saisie manuelle', en: 'Manual entry' },
  manualHint: {
    fr: 'Aucune valeur n’a pu être extraite. Saisissez les résultats manuellement.',
    en: 'No value could be extracted. Enter the results manually.',
  },
  sampleDate: { fr: 'Date de prélèvement', en: 'Sample date' },
  resultDate: { fr: 'Date du résultat', en: 'Result date' },
  status: { fr: 'Statut', en: 'Status' },
  analyte: { fr: 'Analyte', en: 'Analyte' },
  customLabel: { fr: 'Libellé du rapport', en: 'Label from report' },
  customUnit: { fr: 'Unité du rapport', en: 'Unit from report' },
  value: { fr: 'Valeur', en: 'Value' },
  unit: { fr: 'Unité', en: 'Unit' },
  refLow: { fr: 'Réf. basse', en: 'Ref low' },
  refHigh: { fr: 'Réf. haute', en: 'Ref high' },
  addRow: { fr: '+ Ajouter un analyte', en: '+ Add analyte' },
  remove: { fr: 'Retirer', en: 'Remove' },
  submit: { fr: 'Importer', en: 'Import' },
  reset: { fr: 'Recommencer', en: 'Start over' },
  savedPending: {
    fr: 'Envoyé au médecin pour vérification',
    en: 'Sent to the doctor for verification',
  },
  saved: { fr: 'Enregistré', en: 'Saved' },
  importAnother: { fr: 'Importer un autre', en: 'Import another' },
  g6pdDetected: { fr: 'G6PD détecté sur le rapport', en: 'G6PD detected on the report' },
  applyToRecord: { fr: 'Appliquer au dossier du patient', en: 'Apply to the patient record' },
} as const;

// G6PD status → bilingual label.
const G6PD_LABEL: Record<string, { fr: string; en: string }> = {
  normal: { fr: 'normal', en: 'normal' },
  abnormal: { fr: 'anormal (déficit)', en: 'abnormal (deficient)' },
  pending: { fr: 'en attente', en: 'pending' },
  not_applicable: { fr: 'non applicable', en: 'not applicable' },
  unknown: { fr: 'inconnu', en: 'unknown' },
};

interface ExtractSuggestion {
  key: AnalyteKey;
  customLabel?: string;
  value: string;
  unit: AnalyteUnit;
  customUnit?: string;
  referenceLow?: string;
  referenceHigh?: string;
  confidence: 'low';
  rawMatch: string;
}
interface ExtractResponse {
  extracted: boolean;
  reason?: string;
  suggestions: ExtractSuggestion[];
  sampleDateGuess?: string;
  baseline?: { g6pdStatus?: string };
}
interface ImportResponse {
  result: unknown;
  needsVerification: boolean;
}

interface AnalyteRow {
  key: AnalyteKey;
  customLabel: string;
  unit: AnalyteUnit;
  customUnit: string;
  value: string;
  referenceLow: string;
  referenceHigh: string;
}

// NFS preset rows the review form always starts with.
const NFS_PRESET: { key: AnalyteKey; unit: AnalyteUnit }[] = [
  { key: 'hemoglobin', unit: 'g/dL' },
  { key: 'wbc', unit: '10e3/uL' },
  { key: 'neutrophils_abs', unit: 'per_mm3' },
  { key: 'platelets', unit: '10e3/uL' },
];

const today = () => new Date().toISOString().slice(0, 10);
const fmtUnit = (u: string) => u.replace('per_mm3', '/mm³');

/** Build the editable rows: NFS preset, pre-filled from any matching suggestion. */
function buildRows(suggestions: ExtractSuggestion[]): AnalyteRow[] {
  const byKey = new Map<AnalyteKey, ExtractSuggestion>();
  for (const s of suggestions) if (!byKey.has(s.key)) byKey.set(s.key, s);

  const rows: AnalyteRow[] = NFS_PRESET.map((p) => {
    const hit = byKey.get(p.key);
    return {
      key: p.key,
      customLabel: hit?.customLabel ?? '',
      unit: hit?.unit ?? p.unit,
      customUnit: hit?.customUnit ?? '',
      value: hit?.value ?? '',
      referenceLow: hit?.referenceLow ?? '',
      referenceHigh: hit?.referenceHigh ?? '',
    };
  });
  // Append any extracted analytes that are not part of the NFS preset —
  // carrying the report's own label (customLabel) and reference range.
  const presetKeys = new Set(NFS_PRESET.map((p) => p.key));
  for (const s of suggestions) {
    if (presetKeys.has(s.key) && s.key !== 'custom') continue;
    rows.push({
      key: s.key,
      customLabel: s.customLabel ?? '',
      unit: s.unit,
      customUnit: s.customUnit ?? '',
      value: s.value,
      referenceLow: s.referenceLow ?? '',
      referenceHigh: s.referenceHigh ?? '',
    });
  }
  return rows;
}

export function LabImport({ onDone, className }: { onDone?: () => void; className?: string }) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const isClinician = user?.role === 'clinician' || user?.role === 'owner_admin';
  const lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  // Phases: pick → review (form shown) → done
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<boolean>(false);
  const [rows, setRows] = useState<AnalyteRow[]>([]);
  const [sampleDate, setSampleDate] = useState(today());
  const [resultDate, setResultDate] = useState('');
  const [status, setStatus] = useState('received');
  // Reference data the report mentions beyond analytes (e.g. G6PD).
  const [g6pd, setG6pd] = useState<string | null>(null);
  const [applyG6pd, setApplyG6pd] = useState(true);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);

  function resetAll() {
    setAttachmentId(null);
    setExtracted(false);
    setRows([]);
    setSampleDate(today());
    setResultDate('');
    setStatus('received');
    setG6pd(null);
    setApplyG6pd(true);
    setDoneMessage(null);
    setDragging(false);
    setDropError(null);
    uploadM.reset();
    importM.reset();
  }

  function setRow(i: number, patch: Partial<AnalyteRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { key: 'custom', customLabel: '', unit: 'other', customUnit: '', value: '', referenceLow: '', referenceHigh: '' }]);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  }

  // Upload the chosen file then immediately run extraction.
  const uploadM = useMutation({
    mutationFn: async (file: File) => {
      const kind = file.type === 'application/pdf' ? 'lab_pdf' : 'lab_photo';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
        body: fd,
      });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = json?.error ?? {};
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? res.statusText, err.details);
      }
      const id: string = json.attachment.id;
      const ex = await api.post<ExtractResponse>('/labs/extract', { attachmentId: id });
      return { id, ex };
    },
    onSuccess: ({ id, ex }) => {
      setAttachmentId(id);
      setExtracted(ex.extracted && ex.suggestions.length > 0);
      setRows(buildRows(ex.suggestions));
      if (ex.sampleDateGuess) setSampleDate(ex.sampleDateGuess);
      setG6pd(ex.baseline?.g6pdStatus ?? null);
    },
  });

  const importM = useMutation({
    mutationFn: () =>
      api.post<ImportResponse>('/labs/results/import', {
        sampleDate,
        resultDate: resultDate || undefined,
        status,
        isNfsPreset: true,
        sourceAttachmentId: attachmentId ?? undefined,
        // Apply detected reference data (G6PD) only when a clinician opted in.
        baseline: isClinician && applyG6pd && g6pd ? { g6pdStatus: g6pd } : undefined,
        analytes: rows
          .filter((r) => r.value.trim() !== '')
          .map((r) => ({
            key: r.key,
            customLabel: r.key === 'custom' && r.customLabel.trim() ? r.customLabel.trim() : undefined,
            value: r.value.trim(),
            unit: r.unit,
            customUnit: r.unit === 'other' && r.customUnit.trim() ? r.customUnit.trim() : undefined,
            referenceLow: r.referenceLow.trim() || undefined,
            referenceHigh: r.referenceHigh.trim() || undefined,
          })),
      }),
    onSuccess: (res) => {
      setDoneMessage(res.needsVerification ? tr(L.savedPending) : tr(L.saved));
      onDone?.();
    },
  });

  function acceptFile(file: File | undefined | null) {
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!ok) {
      setDropError(tr(L.unsupportedFile));
      return;
    }
    setDropError(null);
    uploadM.mutate(file);
  }
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const hasForm = attachmentId !== null && doneMessage === null;
  const busy = uploadM.isPending;

  return (
    <div className={className}>
      {/* --- Success state --- */}
      {doneMessage !== null && (
        <div className="space-y-3">
          <div className="text-sm rounded-lg px-3 py-2 bg-green-50 border border-green-200 text-green-800" role="status">
            ✓ {doneMessage}
          </div>
          <Button type="button" variant="secondary" onClick={resetAll}>
            {tr(L.importAnother)}
          </Button>
        </div>
      )}

      {/* --- File picker + drag-and-drop (initial) --- */}
      {attachmentId === null && doneMessage === null && (
        <div className="space-y-2">
          <label
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition ${
              dragging ? 'border-brand bg-brand-light/40' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
            } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <span className="text-2xl" aria-hidden>📄</span>
            <span className="text-sm font-medium text-slate-700">
              {dragging ? tr(L.dropActive) : tr(L.drop)}
            </span>
            <span className="text-xs text-slate-400">{tr(L.dropOr)}</span>
            <span className="text-sm text-brand underline">{tr(L.pick)}</span>
            <input type="file" accept="application/pdf,image/*" onChange={onFile} disabled={busy} className="hidden" />
          </label>
          {busy && <p className="text-sm text-slate-400">{tr(L.extracting)}</p>}
          {dropError && <p className="text-sm text-danger">{dropError}</p>}
          {uploadM.isError && <ErrorState error={uploadM.error} fallback={t('common.error')} />}
        </div>
      )}

      {/* --- Review form --- */}
      {hasForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            importM.mutate();
          }}
          className="space-y-3"
        >
          {extracted ? (
            <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
              <strong>{tr(L.draftNotice)}</strong>
              <span className="block text-amber-700">{tr(L.draftHint)}</span>
            </div>
          ) : (
            <div className="text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700">
              <strong>{tr(L.manualEntry)}</strong>
              <span className="block text-slate-500">{tr(L.manualHint)}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
            <Field label={tr(L.sampleDate)}>
              <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
            </Field>
            <Field label={tr(L.resultDate)}>
              <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
            </Field>
            <Field label={tr(L.status)}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {LAB_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`lab.${s}`)}</option>
                ))}
              </Select>
            </Field>
          </div>

          {/* Reference data point detected on the report (e.g. G6PD). */}
          {g6pd && (
            <div className="text-sm rounded-lg px-3 py-2 bg-blue-50 border border-blue-200 text-blue-900">
              <span>
                {tr(L.g6pdDetected)}: <strong>{tr(G6PD_LABEL[g6pd] ?? { fr: g6pd, en: g6pd })}</strong>
              </span>
              {isClinician && (
                <label className="mt-1 flex items-center gap-2 text-blue-800">
                  <input type="checkbox" checked={applyG6pd} onChange={(e) => setApplyG6pd(e.target.checked)} />
                  {tr(L.applyToRecord)}
                </label>
              )}
            </div>
          )}

          <Table headers={[tr(L.analyte), tr(L.value), tr(L.unit), tr(L.refLow), tr(L.refHigh), '']}>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1 pr-2 min-w-40">
                  <Select value={r.key} onChange={(e) => setRow(i, { key: e.target.value as AnalyteKey })}>
                    {ANALYTE_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`analyte.${k}`)}</option>
                    ))}
                  </Select>
                  {r.key === 'custom' && (
                    <input
                      className={`${inputClass} mt-1`}
                      placeholder={tr(L.customLabel)}
                      value={r.customLabel}
                      onChange={(e) => setRow(i, { customLabel: e.target.value })}
                    />
                  )}
                </td>
                <td className="py-1 pr-2">
                  <input className={inputClass} inputMode="decimal" value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} />
                </td>
                <td className="py-1 pr-2 min-w-28">
                  <Select value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value as AnalyteUnit })}>
                    {ANALYTE_UNITS.map((u) => (
                      <option key={u} value={u}>{fmtUnit(u)}</option>
                    ))}
                  </Select>
                  {r.unit === 'other' && (
                    <input
                      className={`${inputClass} mt-1`}
                      placeholder={tr(L.customUnit)}
                      value={r.customUnit}
                      onChange={(e) => setRow(i, { customUnit: e.target.value })}
                    />
                  )}
                </td>
                <td className="py-1 pr-2">
                  <input className={inputClass} inputMode="decimal" value={r.referenceLow} onChange={(e) => setRow(i, { referenceLow: e.target.value })} />
                </td>
                <td className="py-1 pr-2">
                  <input className={inputClass} inputMode="decimal" value={r.referenceHigh} onChange={(e) => setRow(i, { referenceHigh: e.target.value })} />
                </td>
                <td className="py-1 pr-2 whitespace-nowrap">
                  <button type="button" className="text-xs underline text-danger" onClick={() => removeRow(i)}>
                    {tr(L.remove)}
                  </button>
                </td>
              </tr>
            ))}
          </Table>

          <button type="button" className="text-sm underline text-brand" onClick={addRow}>
            {tr(L.addRow)}
          </button>

          {importM.isError && <ErrorState error={importM.error} fallback={t('common.error')} />}

          <div className="flex gap-2">
            <Button type="submit" disabled={importM.isPending || rows.every((r) => r.value.trim() === '')}>
              {tr(L.submit)}
            </Button>
            <Button type="button" variant="secondary" onClick={resetAll}>
              {tr(L.reset)}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export { L as labImportStrings };
